import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Users, ExternalLink, Copy, CheckSquare, Square, Megaphone, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { copyTextReliable } from "@/lib/clipboard";
import FbShareTuto from "@/components/FbShareTuto";

export default function FbGroupShare({ text, shareUrl, cityHint = "", onShared, compact = false, small = false, showTopCopy = false, testId = "fb-group-share" }) {
  const [groups, setGroups] = useState([]);
  const [sel, setSel] = useState({});
  // Replié par défaut PARTOUT (menu déroulant) : l'outil complet se déploie au clic sur l'en-tête.
  const [open, setOpen] = useState(false);

  const hint = (cityHint || "").trim().toLowerCase();
  const isMatch = (g) => !!hint && (((g.city || "").toLowerCase().includes(hint)) || ((g.name || "").toLowerCase().includes(hint)));

  useEffect(() => {
    api.get("/facebook-groups").then((r) => {
      let gs = (r.data.items || []).filter((g) => g.url);
      if (hint) {
        gs = [...gs].sort((a, b) => (isMatch(b) ? 1 : 0) - (isMatch(a) ? 1 : 0));
        const presel = {};
        gs.forEach((g) => { if (isMatch(g)) presel[g.id || g.url] = true; });
        setSel(presel);
      }
      setGroups(gs);
    }).catch(() => {});
  }, [hint]); // eslint-disable-line

  const defaultCombined = useMemo(() => [text, shareUrl].filter(Boolean).join("\n"), [text, shareUrl]);
  const [draft, setDraft] = useState("");
  const [copied, setCopied] = useState(false);
  useEffect(() => { setDraft(defaultCombined); }, [defaultCombined]);
  const combined = draft;
  const selected = groups.filter((g) => sel[g.id || g.url]);
  const allSelected = groups.length > 0 && selected.length === groups.length;

  if (!groups.length) return null;

  const key = (g) => g.id || g.url;
  const toggle = (g) => setSel((s) => ({ ...s, [key(g)]: !s[key(g)] }));
  const toggleAll = () => {
    if (allSelected) return setSel({});
    const n = {};
    groups.forEach((g) => { n[key(g)] = true; });
    setSel(n);
  };

  // Copie fiable (voir lib/clipboard) : fonctionne même juste avant l'ouverture d'onglets.
  const copyText = () => {
    const ok = copyTextReliable(combined);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Texte + lien copiés ! Collez-les (Ctrl/Cmd+V) dans votre groupe.", { duration: 6000 });
    } else {
      toast.error("Copie automatique impossible — sélectionnez le texte ci-dessus et copiez-le manuellement.", { duration: 7000 });
    }
    return ok;
  };

  const openSelected = () => {
    if (!selected.length) { toast.message("Sélectionnez au moins un groupe ci-dessus."); return; }
    // On copie AVANT d'ouvrir les onglets (sinon la perte de focus annule la copie).
    const ok = copyTextReliable(combined);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success(`Texte + lien copiés. ${selected.length} groupe(s) ouvert(s) : il ne reste qu'à coller (Ctrl/Cmd+V) puis publier.`, { duration: 9000 });
    } else {
      toast.error("Copie automatique impossible — utilisez d'abord le bouton « Copier le texte + le lien », puis collez dans chaque groupe.", { duration: 9000 });
    }
    selected.forEach((g) => window.open(g.url, "_blank", "noopener"));
    onShared?.();
  };

  return (
    <div className="mt-6" data-testid={testId}>
      <button onClick={() => setOpen((o) => !o)} data-testid="fb-group-share-toggle"
              className="w-full flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-2xl px-4 py-3 text-left hover:border-[#1877F2]/50 transition-colors">
        <span className="flex items-center gap-2 font-heading font-bold text-slate-900 text-sm sm:text-base">
          <Megaphone size={18} className="text-[#1877F2]" /> Partager dans mes groupes Facebook
          <span className="text-xs font-normal text-slate-400">({groups.length})</span>
        </span>
        <ChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className={`mt-2 bg-white border border-slate-200 rounded-2xl ${small ? "p-4" : "p-5"}`}>
      <p className={`text-slate-600 mt-1 ${small ? "text-xs" : "text-sm"}`}>
        {small
          ? "Copiez le post ci-dessous (texte + lien), puis collez-le dans vos groupes Facebook."
          : <>Facebook n'autorise pas le remplissage automatique d'un post de groupe. Pas de souci : on fait tout le travail à votre place en <b>3 étapes simples</b> ci-dessous. 👇</>}
      </p>
      <FbShareTuto className="mt-3" />
      {showTopCopy && (
        <button onClick={copyText} data-testid="fb-group-copy-top"
                className={`mt-3 w-full flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold border-2 transition-colors ${copied ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-300 text-slate-800 hover:bg-slate-50"}`}>
          {copied ? <Check size={18} /> : <Copy size={18} />}
          {copied ? "Texte + lien copiés ✓" : "Copier le texte + le lien"}
        </button>
      )}

      <div className="mt-3 flex items-center gap-2 text-sm font-bold text-slate-900">
        <span className="shrink-0 grid place-items-center w-6 h-6 rounded-full bg-[#E05638] text-white text-xs">1</span>
        Vérifiez (et modifiez si besoin) le texte à publier
      </div>
      <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="fb-post-preview">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Post prêt-à-coller (modifiable)</span>
          <span className="text-[11px] text-slate-400">{draft.length} car.</span>
        </div>
        <textarea
          data-testid="fb-post-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={6}
          className="w-full text-sm text-slate-700 bg-white border border-slate-200 rounded-lg p-2.5 resize-y focus:outline-none focus:ring-2 focus:ring-[#E05638]/40"
        />
        <button
          onClick={() => setDraft(defaultCombined)}
          data-testid="fb-post-reset"
          className="mt-1.5 text-[11px] font-semibold text-slate-500 hover:text-slate-700 underline"
        >
          Réinitialiser le texte proposé
        </button>
      </div>

      <div className="mt-5 flex items-center gap-2 text-sm font-bold text-slate-900">
        <span className="shrink-0 grid place-items-center w-6 h-6 rounded-full bg-[#E05638] text-white text-xs">2</span>
        Choisissez les groupes où publier
      </div>
      <div className="mt-2 flex items-center justify-between">
        <button onClick={toggleAll} data-testid="fb-group-toggle-all"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900">
          {allSelected ? <CheckSquare size={16} className="text-[#E05638]" /> : <Square size={16} />}
          {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
        </button>
        <span className="text-xs text-slate-400">{selected.length}/{groups.length} sélectionné(s)</span>
      </div>

      <div className="mt-2 max-h-64 overflow-auto rounded-xl border border-slate-100 divide-y divide-slate-100" data-testid="fb-group-list">
        {groups.map((g) => {
          const checked = !!sel[key(g)];
          return (
            <div key={key(g)} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
              <button onClick={() => toggle(g)} data-testid={`fb-group-check-${key(g)}`} className="shrink-0">
                {checked ? <CheckSquare size={18} className="text-[#E05638]" /> : <Square size={18} className="text-slate-300" />}
              </button>
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => toggle(g)}>
                <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-2">
                  <span className="truncate">{g.name || g.url}</span>
                  {isMatch(g) && <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-[#E05638]">Groupe local</span>}
                </div>
                <div className="text-[11px] text-slate-400 flex items-center gap-2">
                  {g.city && <span>{g.city}</span>}
                  {g.member_count > 0 && <span className="inline-flex items-center gap-1"><Users size={11} /> {g.member_count.toLocaleString("fr-FR")}</span>}
                </div>
              </div>
              <a href={g.url} target="_blank" rel="noreferrer" data-testid={`fb-group-open-${key(g)}`}
                 className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline">
                Ouvrir <ExternalLink size={12} />
              </a>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center gap-2 text-sm font-bold text-slate-900">
        <span className="shrink-0 grid place-items-center w-6 h-6 rounded-full bg-[#E05638] text-white text-xs">3</span>
        Copiez le texte, puis publiez dans vos groupes
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3">
        <button onClick={copyText} data-testid="fb-group-copy"
                className={`flex items-center justify-center gap-2.5 h-14 rounded-2xl text-base font-bold border-2 transition-colors ${copied ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-300 text-slate-800 hover:bg-slate-50"}`}>
          {copied ? <Check size={22} /> : <Copy size={20} />}
          {copied ? "Texte + lien copiés ✓" : "Copier le texte + le lien"}
        </button>
        <button onClick={openSelected} data-testid="fb-group-open-selected"
                className="flex items-center justify-center gap-2.5 h-16 rounded-2xl btn-shine text-white text-base font-extrabold hover:opacity-90 disabled:opacity-50"
                disabled={!selected.length}>
          <Megaphone size={22} />
          {selected.length ? `Publier dans les ${selected.length} groupe(s)` : "Sélectionnez au moins un groupe"}
        </button>
      </div>
      <div className="mt-3 rounded-xl bg-blue-50 border border-blue-100 p-3 text-xs text-slate-600 leading-relaxed">
        <b className="text-slate-800">Comment ça marche ?</b> En cliquant sur <b>« Publier dans les groupes »</b>, le texte
        est automatiquement <b>copié</b> et chaque groupe s'ouvre dans un nouvel onglet. Dans chaque onglet,
        cliquez dans la zone « Exprimez-vous… », faites <b>Coller</b> (<kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px]">Ctrl</kbd>+<kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px]">V</kbd> sur PC, <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px]">⌘</kbd>+<kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px]">V</kbd> sur Mac/iPhone) puis <b>Publier</b>.
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        Astuce : si votre navigateur bloque l'ouverture de plusieurs onglets, autorisez les fenêtres pop-up pour ce site,
        ou utilisez le lien « Ouvrir » de chaque groupe.
      </p>
      </div>
      )}
    </div>
  );
}
